import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateOptions } from 'react-router-dom';

import { classNames } from 'support/helpers/generic/generic';
import { SelectionCell } from 'components/DataTable/cells/SelectionCell';
import { Table } from 'components/Display/Table/Table';
import { Button } from 'components/Form/Button';

import { getAriaSortValue, getNextSortDirection } from './support/helpers';
import { ButtonAction, ButtonActionProps } from './components/ButtonAction/ButtonAction';
import { DropdownAction, DropdownActionProps } from './components/DropdownAction/DropdownAction';
import { EmptyState } from './components/EmptyState/EmptyState';
import { SortIndicator } from './components/SortIndicator/SortIndicator';
import { SkeletonLoadingRows } from './SkeletonLoadingRows';
import { SortState, TableHeader, TableItem } from './types';

type BulkActionProps<T> = {
  text: string;
  actions: Array<ButtonActionProps<T> | DropdownActionProps<T>>;
};

type ListTableProps<T> = {
  headers: Array<TableHeader>;
  data?: Array<T>;
  isLoading: boolean;
  isLoadingMore?: boolean;
  loadingRowsCount?: number;
  rowBuilder: (data: T, index: number) => Array<TableItem>;
  onRowClick?: (data: T) => void;
  rowClickPath?: (data: T) => string | undefined | { url: string; state?: NavigateOptions['state'] };
  emptyState?: ReactNode;
  pagination?: ReactNode;
  bulkActions?: BulkActionProps<T>;
  sortState?: SortState;
  onSortChange?: (newSortState: SortState) => void;
};

function ListTableWrapper<T>({
  headers,
  data,
  isLoading,
  isLoadingMore,
  loadingRowsCount = 3,
  rowBuilder,
  onRowClick,
  rowClickPath,
  emptyState,
  pagination,
  bulkActions,
  sortState,
  onSortChange,
}: ListTableProps<T>) {
  const { t } = useTranslation();
  const showEmptyState = !data?.length && !isLoading;
  const [selectedItems, setSelectedItems] = useState<Array<T>>([]);
  const toggleAllItems = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        setSelectedItems(data || []);
      } else {
        setSelectedItems([]);
      }
    },
    [data],
  );

  const toggleItem = useCallback((item: T) => {
    setSelectedItems((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
  }, []);

  const clearAllSection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const filteredSelectedItems = useMemo(() => {
    return selectedItems.filter((item) => data?.includes(item));
  }, [selectedItems, data]);

  const renderDynamicActions = useCallback(() => {
    return bulkActions?.actions?.map((action) => {
      switch (action.type) {
        case 'button':
          return <ButtonAction key={action.analyticsId} action={action} selectedItems={filteredSelectedItems} />;
        case 'dropdown':
          return <DropdownAction key={action.analyticsId} action={action} selectedItems={filteredSelectedItems} />;
      }
    });
  }, [bulkActions, filteredSelectedItems]);

  // t('transactions:list.bulk_selected', { count })
  // t('priceCatalogs:partnerList.bulk_selected', { count })
  return (
    <div className="space-y-3">
      {bulkActions && filteredSelectedItems.length > 0 && (
        <div className="sticky top-0 z-10 inline-flex items-center rounded-md border border-gray-200 bg-white px-4 py-2 shadow">
          <p className="mr-6 text-sm text-gray-500">{t(bulkActions.text, { count: filteredSelectedItems.length })}</p>
          {renderDynamicActions()}
          <Button onClick={clearAllSection} variant="secondary" analyticsId={'list_table:bulk_clear'}>
            {t('transactions:list.bulk_clear')}
          </Button>
        </div>
      )}
      <Table>
        {!showEmptyState ? (
          <Table.Head>
            <Table.Row>
              {bulkActions ? (
                <Table.HeadCell className="w-10">
                  <SelectionCell
                    checked={data && filteredSelectedItems.length === data.length}
                    onChange={toggleAllItems}
                    indeterminate={
                      filteredSelectedItems.length > 0 && filteredSelectedItems.length < (data?.length || 0)
                    }
                  />
                </Table.HeadCell>
              ) : null}
              {headers.map((tableHeader: TableHeader) => {
                const isSortable = Boolean(tableHeader.sortable && tableHeader.key);
                const handleSortClick = isSortable
                  ? () => onSortChange?.(getNextSortDirection(sortState, tableHeader.key!))
                  : undefined;

                return (
                  <Table.HeadCell
                    invisible={tableHeader.invisible}
                    key={tableHeader.label || tableHeader.key}
                    className={classNames(tableHeader.className, isSortable ? 'cursor-pointer' : undefined)}
                    alignRight={tableHeader.alignRight}
                    tooltip={tableHeader.tooltip}
                    onClick={handleSortClick}
                    aria-sort={getAriaSortValue(sortState, tableHeader.key ?? '', isSortable)}
                  >
                    {tableHeader.label}
                    {isSortable && <SortIndicator sortState={sortState} columnKey={tableHeader.key!} />}
                  </Table.HeadCell>
                );
              })}
            </Table.Row>
          </Table.Head>
        ) : null}
        <Table.Body isLoading={isLoading}>
          {data?.map((item, index) => {
            const rowData = rowBuilder(item, index);
            return (
              <Table.Row
                key={rowData[0].key}
                onClick={
                  onRowClick || rowClickPath
                    ? () => {
                        onRowClick?.(item);
                      }
                    : undefined
                }
                hover={Boolean(rowClickPath?.(item)) || Boolean(onRowClick)}
              >
                {bulkActions ? (
                  <Table.Cell>
                    <SelectionCell checked={filteredSelectedItems.includes(item)} onChange={() => toggleItem(item)} />
                  </Table.Cell>
                ) : null}
                {rowData.map((cell: TableItem) => {
                  return (
                    <Table.Cell key={cell.key} to={rowClickPath?.(item)} className={cell.className} {...cell.options}>
                      {cell.element}
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            );
          })}

          {isLoadingMore && (
            <SkeletonLoadingRows nRows={loadingRowsCount} nCols={headers.length} withBulkActionsCol={!!bulkActions} />
          )}

          {showEmptyState ? emptyState : null}
        </Table.Body>
      </Table>
      {showEmptyState && emptyState ? null : pagination}
    </div>
  );
}

export const ListTable = Object.assign(ListTableWrapper, { EmptyState });
